import React, { useState, useMemo } from 'react';

import { Loader2, Search, X } from 'lucide-react';

import { useGetDocs } from '@/api/queries/useGetDocs';
import { useGetDocumentChunks } from '@/api/queries/useGetDocumentChunks';
import PDFViewer from '@/components/organisms/PDFViewer';
import type { BoundingBoxWithId } from '@/types';

interface DocChunkBrowserProps {
  caseId: string;
  onSelectChunk: (chunkId: string) => void;
  currentlyAssignedChunkId?: string;
}

const DocChunkBrowser: React.FC<DocChunkBrowserProps> = ({ caseId, onSelectChunk, currentlyAssignedChunkId }) => {
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all documents for the case (including unprocessed for manual labeling workflows)
  const { data: docsData, isLoading: isLoadingDocs } = useGetDocs({
    caseId,
    page: 1,
    pageSize: 1000, // Get all docs
    searchQuery: '',
  });

  // Fetch chunks for selected document
  const { data: chunksData, isLoading: isLoadingChunks } = useGetDocumentChunks(
    caseId,
    selectedDocId,
    1,
    1000, // Get all chunks
  );

  const docs = docsData?.docs || [];
  const chunks = chunksData?.data || [];

  // Filter documents based on search query
  const filteredDocs = useMemo(() => {
    if (!searchQuery.trim()) return docs;
    const query = searchQuery.toLowerCase();
    return docs.filter((doc) => doc.file.name.toLowerCase().includes(query));
  }, [docs, searchQuery]);

  // Convert chunks to highlights for PDFViewer
  const highlights: BoundingBoxWithId[] = useMemo(() => {
    const result: BoundingBoxWithId[] = [];
    chunks.forEach((chunk) => {
      if (chunk.boundingBoxes) {
        chunk.boundingBoxes.forEach((box) => {
          result.push({
            ...box,
            id: chunk.id,
          });
        });
      }
    });
    return result;
  }, [chunks]);

  // Get selected document details
  const selectedDoc = docs.find((doc) => doc.id === selectedDocId);

  const handleSelectDoc = (docId: string) => {
    setSelectedDocId(docId);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    setSelectedDocId(null);
    setSearchQuery('');
  };

  return (
    <div className="flex h-full flex-col">
      {/* Document Selector */}
      <div className="mb-3">
        {/* Selected Document Display */}
        {selectedDocId && selectedDoc ? (
          <div className="flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2">
            <span className="text-sm text-gray-900">{selectedDoc.file.name}</span>
            <button onClick={handleClearSelection} className="text-gray-400 transition-colors hover:text-gray-600">
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pr-3 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                disabled={isLoadingDocs}
              />
            </div>

            {/* Document List */}
            {searchQuery.trim() && (
              <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                {filteredDocs.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">No documents found.</div>
                ) : (
                  filteredDocs.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => handleSelectDoc(doc.id)}
                      className="w-full px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50"
                    >
                      {doc.file.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Document Viewer */}
      <div className="flex-1 overflow-hidden">
        {isLoadingDocs ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        ) : !selectedDocId ? (
          <div className="py-8 text-center text-sm text-gray-500">Select a document to view its chunks.</div>
        ) : isLoadingChunks ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        ) : chunks.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">No chunks found for this document.</div>
        ) : (
          <div className="flex h-full flex-col rounded-md border border-gray-300 bg-white">
            <div className="flex-grow overflow-hidden">
              <div className="relative h-full">
                <PDFViewer
                  caseId={caseId}
                  docId={selectedDocId}
                  highlights={highlights}
                  onHighlightClick={onSelectChunk}
                  hideFeatureButtons={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocChunkBrowser;
